import React from 'react';
import { useTranslation } from 'react-i18next';
import Icon from '../../atoms/Icon/Icon';
import Dialog from '../Dialog/Dialog';

/**
 * DialogImage — v2 molecule.
 *
 * Modal full-screen para previsualizar una imagen. Compone {@link Dialog}
 * con `size="all-screen"` y `isHeaderHidden`, y añade un botón de cierre
 * propio sobre la imagen.
 *
 * Accesibilidad (WCAG 2.1 AA):
 *  - `alt` es obligatorio (WCAG 1.1.1). Si la imagen es decorativa, pasa
 *    `alt=""` explícitamente como opt-in válido.
 *  - El dialog hereda focus trap, cierre por ESC y `aria-modal`.
 *  - El `aria-label` del dialog reproduce el `alt` para que los lectores
 *    de pantalla anuncien el contenido al abrir el modal.
 *  - El botón de cierre tiene `aria-label={t('actions:Close')}` y es
 *    tabbable con foco visible.
 */

type DialogImageProps = Readonly<{
  alt: string;
  isOpen: boolean;
  onClose: () => void;
  src: string;
}>;

function DialogImage({ alt, isOpen, onClose, src }: DialogImageProps): React.JSX.Element {
  const { t } = useTranslation(['actions', 'common']);
  // Cuando la imagen es decorativa (alt=""), el dialog sigue necesitando un
  // nombre accesible (WCAG aria-dialog-name) para anunciarse al abrirse.
  const dialogAriaLabel = alt.length > 0 ? alt : t('common:Image preview');

  return (
    <Dialog ariaLabel={dialogAriaLabel} isHeaderHidden isOpen={isOpen} onClose={onClose} size="all-screen">
      <div className="dialog-image">
        <img alt={alt} className="dialog-image__image" src={src} />
        <button aria-label={t('actions:Close')} className="dialog-image__close" onClick={onClose} type="button">
          <Icon name="close" />
        </button>
      </div>
    </Dialog>
  );
}

export default DialogImage;
